import React, { useEffect, useState, useMemo } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError, API_BASE } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { FileSpreadsheet, FileText } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { LibroPagination } from "../components/LibroPagination";

export default function LibroMayor() {
  const [cuentas, setCuentas] = useState([]);
  const [cuentaId, setCuentaId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState(todayISO());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cuentas");
        const imputables = data.filter((c) => c.imputable);
        setCuentas(imputables);
        if (imputables[0]) setCuentaId(imputables[0].id);
      } catch (e) {
        toast.error(extractError(e));
      }
    })();
  }, []);

  useEffect(() => {
    if (!cuentaId) return;
    (async () => {
      setLoading(true);
      try {
        const params = { page, page_size: pageSize };
        if (desde) params.desde = desde;
        if (hasta) params.hasta = hasta;
        const { data } = await api.get(`/libro-mayor/${cuentaId}`, { params });
        setData(data);
      } catch (e) {
        toast.error(extractError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [cuentaId, desde, hasta, page, pageSize]);

  const sortedCuentas = useMemo(
    () => [...cuentas].sort((a, b) => a.codigo.localeCompare(b.codigo)),
    [cuentas]
  );

  const exportParams = () => {
    const params = new URLSearchParams();
    if (desde) params.set("desde", desde);
    if (hasta) params.set("hasta", hasta);
    return params.toString();
  };

  return (
    <Page>
      <PageHeader title="Libro mayor" subtitle="Movimientos detallados por cuenta" testid="libro-mayor-header"
        actions={cuentaId && (
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setPage(1); }}
              data-testid="lm-desde" className="px-2 py-1.5 border border-border rounded-sm text-sm" />
            <span className="text-muted-foreground text-xs">→</span>
            <input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setPage(1); }}
              data-testid="lm-hasta" className="px-2 py-1.5 border border-border rounded-sm text-sm" />
            <button onClick={() => window.open(`${API_BASE}/libro-mayor/${cuentaId}/excel?${exportParams()}`, "_blank")}
              data-testid="lm-excel" className="inline-flex items-center gap-1.5 border border-success text-success hover:bg-success/10 text-sm px-3 py-1.5 rounded-sm">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>
            <button onClick={() => window.open(`${API_BASE}/libro-mayor/${cuentaId}/pdf?${exportParams()}`, "_blank")}
              data-testid="lm-pdf" className="inline-flex items-center gap-1.5 border border-destructive text-destructive hover:bg-destructive/10 text-sm px-3 py-1.5 rounded-sm">
              <FileText className="h-4 w-4" /> PDF
            </button>
          </div>
        )}
      />

      <div className="bg-card border border-border rounded-md p-4 mb-4">
        <label className="label-caps block mb-1.5">Cuenta</label>
        <Select value={cuentaId} onValueChange={(v) => { setCuentaId(v); setPage(1); }}>
          <SelectTrigger data-testid="mayor-cuenta-select" className="max-w-md text-sm">
            <SelectValue placeholder="Seleccionar cuenta..." />
          </SelectTrigger>
          <SelectContent>
            {sortedCuentas.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                <span className="font-mono mr-2">{c.codigo}</span>{c.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : !data ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Selecciona una cuenta</div>
        ) : (
          <>
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="label-caps">Cuenta</div>
                <div className="font-display text-lg font-medium mt-0.5">
                  <span className="font-mono text-muted-foreground text-base mr-2">{data.cuenta.codigo}</span>
                  {data.cuenta.nombre}
                </div>
              </div>
              <div className="text-right">
                <div className="label-caps">Saldo final</div>
                <div className="num text-2xl font-semibold text-primary" data-testid="mayor-saldo-final">
                  {fmtCLP(data.saldo_final)}
                </div>
              </div>
            </div>
            {data.movimientos.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm" data-testid="mayor-empty">
                Sin movimientos en el período
              </div>
            ) : (
              <table className="acc-table" data-testid="mayor-table">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>Fecha</th>
                    <th style={{ width: 70 }} className="num">N°</th>
                    <th>Glosa</th>
                    <th className="num" style={{ width: 130 }}>Débito</th>
                    <th className="num" style={{ width: 130 }}>Crédito</th>
                    <th className="num" style={{ width: 140 }}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.movimientos.map((m, i) => (
                    <tr key={i}>
                      <td>{fmtDate(m.fecha)}</td>
                      <td className="num">#{m.numero}</td>
                      <td>
                        {m.glosa}
                        {m.glosa_linea && <span className="text-[11px] text-muted-foreground/70 ml-2">— {m.glosa_linea}</span>}
                      </td>
                      <td className="num">{m.debito > 0 ? fmtCLP(m.debito) : ""}</td>
                      <td className="num">{m.credito > 0 ? fmtCLP(m.credito) : ""}</td>
                      <td className="num font-semibold">{fmtCLP(m.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <LibroPagination
              pagination={data.pagination}
              pageSize={pageSize}
              onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
              onPageChange={setPage}
              testidPrefix="mayor"
            />
          </>
        )}
      </div>
    </Page>
  );
}
